/**
 * useMutationReplay — replays queued offline mutations when connectivity returns.
 *
 * Listens for the browser "online" event and attempts to replay all
 * queued mutations. Mutations that fail again (network) stay in the queue;
 * mutations that succeed or get a 4xx (non-retryable) are removed.
 *
 * Max 5 attempts per mutation — after that it's discarded with a warning.
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { API_BASE } from "../utils/apiClient";
import { publicAnonKey } from "../../../utils/supabase/info";
import {
  getQueuedMutations,
  dequeueMutation,
  incrementMutationAttempts,
  clearMutationQueue,
  type QueuedMutation,
} from "../utils/offlineCache";
import { toast } from "sonner";

const MAX_ATTEMPTS = 5;
const SESSION_TOKEN_KEY = "zap_session_token";

function getToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function useMutationReplay() {
  const isReplayingRef = useRef(false);
  const [pendingCount, setPendingCount] = useState(() => getQueuedMutations().length);

  const replayQueue = useCallback(async () => {
    if (isReplayingRef.current) return;
    const queue = getQueuedMutations();
    if (queue.length === 0) return;

    isReplayingRef.current = true;
    console.log(`[MutationReplay] Replaying ${queue.length} queued mutation(s)...`);

    let succeeded = 0;
    let failed = 0;
    let discarded = 0;

    for (const mut of queue) {
      if (mut.attempts >= MAX_ATTEMPTS) {
        console.warn(`[MutationReplay] Discarding mutation after ${MAX_ATTEMPTS} attempts:`, mut.path);
        dequeueMutation(mut.id);
        discarded++;
        continue;
      }

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${publicAnonKey}`,
        };
        const token = getToken();
        if (token) headers["X-Session-Token"] = token;

        const res = await fetch(`${API_BASE}${mut.path}`, {
          method: mut.method,
          headers,
          body: mut.body || undefined,
        });

        if (res.ok) {
          dequeueMutation(mut.id);
          succeeded++;
        } else if (res.status >= 400 && res.status < 500) {
          // 4xx — non-retryable, discard
          console.warn(`[MutationReplay] Discarding mutation (${res.status}):`, mut.path);
          dequeueMutation(mut.id);
          discarded++;
        } else {
          // 5xx — keep in queue, increment attempts
          incrementMutationAttempts(mut.id);
          failed++;
        }
      } catch {
        // Network error — keep in queue
        incrementMutationAttempts(mut.id);
        failed++;
      }
    }

    isReplayingRef.current = false;
    setPendingCount(getQueuedMutations().length);

    // Show summary toast
    const parts: string[] = [];
    if (succeeded > 0) parts.push(`${succeeded} \u05D4\u05E6\u05DC\u05D9\u05D7\u05D5`);
    if (failed > 0) parts.push(`${failed} \u05E0\u05DB\u05E9\u05DC\u05D5`);
    if (discarded > 0) parts.push(`${discarded} \u05E0\u05DE\u05D7\u05E7\u05D5`);

    if (parts.length > 0) {
      const msg = `\u05E1\u05E0\u05DB\u05E8\u05D5\u05DF \u05E4\u05E2\u05D5\u05DC\u05D5\u05EA \u05D0\u05D5\u05E4\u05DC\u05D9\u05D9\u05DF: ${parts.join(", ")}`;
      if (failed > 0) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      // Small delay to let the network stabilize
      setTimeout(replayQueue, 2000);
    };

    window.addEventListener("online", handleOnline);

    // Also check on mount if we have pending mutations and we're online
    if (navigator.onLine && getQueuedMutations().length > 0) {
      setTimeout(replayQueue, 3000);
    }

    // Periodic check for pending count
    const interval = setInterval(() => {
      setPendingCount(getQueuedMutations().length);
    }, 10_000);

    return () => {
      window.removeEventListener("online", handleOnline);
      clearInterval(interval);
    };
  }, [replayQueue]);

  return {
    pendingCount,
    replayNow: replayQueue,
    clearQueue: () => {
      clearMutationQueue();
      setPendingCount(0);
    },
  };
}
